<script setup lang="ts">
  import { computed } from 'vue'
  import { IconClose } from '@gat-ship/icons'
  import { getModalRegistration, useModalStack } from '../../lib/modal-stack'

  // Tabs strip for the central modal stack (central-modal-host plan, Phase 3 +
  // 6). Lists the open modals; clicking a tab raises it (focus = move-to-end →
  // top z), the × closes it. Every registered stack entry appears — both
  // persistent and ephemeral (persist:false) kinds. Transient confirms/pickers
  // that aren't registered kinds are NOT stack entries (they float above via the
  // z-band), so they never clutter the strip. Shown only when ≥2 are open (one
  // needs no switcher). Mount once at the app shell, next to <ModalStackHost>.
  const stack = useModalStack()

  const tabs = computed(() =>
    stack.entries.value
      .filter((entry) => getModalRegistration(entry.kind) != null)
      .map((entry) => ({
        kind: entry.kind,
        label: getModalRegistration(entry.kind)?.label ?? entry.kind
      }))
  )

  const topKind = computed(() => stack.entries.value.at(-1)?.kind)

  // Float just above the top modal so the strip stays reachable.
  const zIndex = computed(() => stack.topZ.value + 1)
</script>
<template>
  <div
    v-if="tabs.length >= 2"
    class="bg-background border-border pointer-events-auto fixed bottom-3 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-lg border p-1 shadow-lg"
    :style="{ zIndex }"
    data-testid="modal-stack-tabs"
  >
    <div
      v-for="tab in tabs"
      :key="tab.kind"
      class="data-[active=true]:bg-accent flex items-center gap-1 rounded-md pl-1"
      :data-active="tab.kind === topKind"
      :data-kind="tab.kind"
    >
      <button
        type="button"
        class="text-foreground hover:bg-accent/60 cursor-pointer rounded px-2 py-1 text-sm"
        @click="stack.raise(tab.kind)"
      >
        {{ tab.label }}
      </button>
      <button
        type="button"
        class="text-muted-foreground hover:text-foreground hover:bg-accent cursor-pointer rounded p-1"
        :aria-label="`Close ${tab.label}`"
        @click="stack.close(tab.kind)"
      >
        <IconClose class="size-3.5" />
      </button>
    </div>
  </div>
</template>
